/**
 * Click-and-drag horizontal scrolling.
 *
 * A native `overflow-x: auto` track already scrolls with touch and trackpads, but
 * a mouse press-and-drag does nothing — leaving prev/next buttons as the only
 * desktop affordance. Pointer events fill that gap.
 *
 * Two ways to use it:
 *   1. `new DragScroll(element)` — attach to any scrollable element.
 *   2. `<drag-scroll>` — wraps a scrollable child; the element itself scrolls if
 *      no `[data-drag-scroll-target]` child is present.
 */
/* Guarded because this file is included per section instance and by more than
   one section, so it can execute several times on a page. The classes live
   inside the block, which keeps them block-scoped and makes a second run a
   no-op instead of an "already declared" SyntaxError. */
if (!customElements.get('drag-scroll')) {
  class DragScroll {
    static DRAG_THRESHOLD = 10;

    constructor(element) {
      if (!element || element.dragScroll) return element?.dragScroll;
      element.dragScroll = this;

      this.element = element;
      this.pointerId = null;
      this.startX = 0;
      this.startScrollLeft = 0;
      this.distance = 0;
      this.dragged = false;
      this.snapType = '';

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClick = this.onClick.bind(this);

      element.addEventListener('pointerdown', this.onPointerDown);
      // Capture phase so the click is swallowed before it reaches a card link.
      element.addEventListener('click', this.onClick, true);
      element.addEventListener('dragstart', (event) => {
        if (this.pointerId !== null) event.preventDefault();
      });
      element.classList.add('is-draggable');
    }

    onPointerDown(event) {
      // Reset before any early return, otherwise a stale "dragged" flag from an
      // earlier interaction swallows the next genuine click.
      this.dragged = false;
      this.distance = 0;

      // Touch and pen already scroll natively — only a mouse needs help.
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (this.element.scrollWidth <= this.element.clientWidth) return;

      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.startScrollLeft = this.element.scrollLeft;
      this.snapType = this.element.style.scrollSnapType;

      // Deliberately NOT flagging `is-dragging` yet. It disables pointer-events on
      // links and buttons, and pointerdown fires before mousedown — so setting it
      // here makes the browser resolve mousedown against the track instead of the
      // button under the cursor, and the button never receives a click at all.
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (event.pointerId !== this.pointerId) return;

      const delta = event.clientX - this.startX;
      this.distance = Math.max(this.distance, Math.abs(delta));

      // Don't hijack the pointer until it's clearly a drag, so pressing a button
      // and twitching a few pixels still registers as a click.
      if (this.distance <= DragScroll.DRAG_THRESHOLD) return;

      if (!this.element.classList.contains('is-dragging')) {
        // Now it really is a drag: kill scroll-snap (it fights a programmatic
        // scrollLeft) and stop children swallowing the pointer.
        this.element.style.scrollSnapType = 'none';
        this.element.classList.add('is-dragging');
      }

      this.element.scrollLeft = this.startScrollLeft - delta;
      event.preventDefault();
    }

    onPointerUp(event) {
      if (event.pointerId !== this.pointerId) return;

      // Only a drag that actually moved the track should suppress the click. If the
      // pointer wandered but the track never scrolled (already at an end, or the
      // move was under threshold), the user meant to click.
      this.dragged = Math.abs(this.element.scrollLeft - this.startScrollLeft) > 2;
      this.pointerId = null;
      this.element.style.scrollSnapType = this.snapType;
      this.element.classList.remove('is-dragging');

      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    onClick(event) {
      // A drag that ends on a card must not also follow that card's link.
      if (!this.dragged) return;

      event.preventDefault();
      event.stopPropagation();
      this.dragged = false;
    }
  }

  window.DragScroll = DragScroll;

  class DragScrollElement extends HTMLElement {
    connectedCallback() {
      const target = this.querySelector('[data-drag-scroll-target]') || this;
      new DragScroll(target);
    }
  }

  customElements.define('drag-scroll', DragScrollElement);
}
